import React, { useState, useEffect } from "react";

import { isTransparent } from "@excalidraw/common";

import {
  shouldAllowVerticalAlign,
  suppportsHorizontalAlign,
} from "@excalidraw/element";

import {
  hasBoundTextElement,
  isElbowArrow,
  isFrameLikeElement,
  isImageElement,
  isLinearElement,
  isTextElement,
  toggleAutoLayout,
  updateAutoLayoutProperties,
  applyAutoLayout,
  DEFAULT_AUTO_LAYOUT_PROPERTIES,
  setupAutoLayoutListener,
  reapplyAutoLayoutToAllFrames,
  reorderAutoLayoutElements,
  getAutoLayoutChildren,
  CaptureUpdateAction,
  computeBoundTextPosition,
  getBoundTextElement,
  newElementWith,
} from "@excalidraw/element";

import { hasStrokeColor, toolIsArrow } from "@excalidraw/element";

import type {
  ExcalidrawElement,
  ExcalidrawFrameElement,
  NonDeletedElementsMap,
  NonDeletedSceneElementsMap,
  AutoLayoutDirection,
  AutoLayoutAlignmentPosition,
  AutoLayoutGap,
  AutoLayoutResizing,
} from "@excalidraw/element/types";

import { alignActionsPredicate } from "../actions/actionAlign";

import { t } from "../i18n";
import {
  canChangeRoundness,
  canHaveArrowheads,
  getTargetElements,
  hasBackground,
  hasStrokeStyle,
  hasStrokeWidth,
} from "../scene";

import { PropertySection } from "./PropertySection";
import { Switch } from "./Switch";

import { useDevice, useExcalidrawAppState } from "./App";
import Collapsible from "./Stats/Collapsible";

// Import Stats components for shape properties
import Position from "./Stats/Position";
import Dimension from "./Stats/Dimension";
import Angle from "./Stats/Angle";
import MultiPosition from "./Stats/MultiPosition";
import MultiDimension from "./Stats/MultiDimension";
import MultiAngle from "./Stats/MultiAngle";
import Spacing from "./Stats/Spacing";
import DragInput from "./Stats/DragInput";
import { getAtomicUnits } from "./Stats/utils";

import {
  alignmentDotIcon,
  alignLeftIcon,
  alignCenterIcon,
  alignRightIcon,
  alignTopIcon,
  alignCenterHorzIcon,
  alignBottomIcon,
} from "./icons";

import {
  autoLayoutHorizontalIcon,
  autoLayoutVerticalIcon,
  SpacingHorizontalIcon,
  paddingTopIcon,
  paddingRightIcon,
  paddingBottomIcon,
  paddingLeftIcon,
  paddingAllIcon,
  adjustmentsHorizontalIcon,
} from "./icons";

import { InlineIcon } from "./InlineIcon";

import type { AppClassProperties, UIAppState } from "../types";
import type { ActionManager } from "../actions/manager";

// Helper function to recalculate all bound text positions
const recalculateAllBoundTextPositions = (
  elements: readonly ExcalidrawElement[],
  elementsMap: NonDeletedElementsMap | NonDeletedSceneElementsMap,
): ExcalidrawElement[] => {
  const updatedElements = [...elements];
  const elementsToUpdate = new Map<string, ExcalidrawElement>();

  // Find all elements that have bound text and recalculate their text positions
  for (const element of elements) {
    if (hasBoundTextElement(element)) {
      const boundTextElement = getBoundTextElement(element, elementsMap);
      if (boundTextElement) {
        try {
          const { x: correctX, y: correctY } = computeBoundTextPosition(
            element,
            boundTextElement,
            elementsMap,
          );

          // Safety check: ensure coordinates are valid
          if (
            typeof correctX === "number" &&
            typeof correctY === "number" &&
            !isNaN(correctX) &&
            !isNaN(correctY)
          ) {
            // Only update if position changed
            if (
              boundTextElement.x !== correctX ||
              boundTextElement.y !== correctY
            ) {
              const correctedBoundText = newElementWith(boundTextElement, {
                x: correctX,
                y: correctY,
              });
              elementsToUpdate.set(boundTextElement.id, correctedBoundText);
            }
          }
        } catch (error) {
          console.warn(
            "recalculateAllBoundTextPositions: error calculating bound text position:",
            error,
          );
        }
      }
    }
  }

  // Apply updates to the elements array
  if (elementsToUpdate.size > 0) {
    return updatedElements.map((element) => 
      elementsToUpdate.get(element.id) || element
    );
  }

  return updatedElements;
};

// Helper functions for the new alignment system
const getAlignmentPositionsForGap = (
  gap: AutoLayoutGap,
): AutoLayoutAlignmentPosition[] => {
  if (gap === "auto") {
    // When gap is auto (space-between), only show 3 positions per axis
    return [
      "top-left",
      "top-center",
      "top-right",
      "center-left",
      "center-center",
      "center-right",
      "bottom-left",
      "bottom-center",
      "bottom-right",
    ];
  }
  // When gap is fixed, show all 9 positions
  return [
    "top-left",
    "top-center",
    "top-right",
    "center-left",
    "center-center",
    "center-right",
    "bottom-left",
    "bottom-center",
    "bottom-right",
  ];
};

// Get alignment icon based on position, active state, hover state, and layout direction
const getAlignmentIcon = (
  position: AutoLayoutAlignmentPosition,
  isActive: boolean,
  isHovered: boolean,
  direction: "horizontal" | "vertical",
): React.JSX.Element => {
  // Show special icons when active or hovered, otherwise show dot
  if (!isActive && !isHovered) {
    return alignmentDotIcon;
  }

  // For horizontal layout direction, use vertical alignment icons
  if (direction === "horizontal") {
    if (position.startsWith("top-")) {
      return alignTopIcon;
    } else if (position.startsWith("center-")) {
      return alignCenterHorzIcon;
    } else if (position.startsWith("bottom-")) {
      return alignBottomIcon;
    }
  }

  // For vertical layout direction, use horizontal alignment icons
  if (position.endsWith("-left")) {
    return alignLeftIcon;
  } else if (position.endsWith("-center")) {
    return alignCenterIcon;
  } else if (position.endsWith("-right")) {
    return alignRightIcon;
  }

  return alignmentDotIcon;
};

// AlignmentGrid component using RadioSelection pattern like corner radius
const AlignmentGrid = ({
  frameElement,
  autoLayoutCallbacks,
  hoveredAlignmentPosition,
  setHoveredAlignmentPosition,
}: {
  frameElement: ExcalidrawFrameElement;
  autoLayoutCallbacks: ReturnType<typeof createAutoLayoutCallbacks>;
  hoveredAlignmentPosition: AutoLayoutAlignmentPosition | null;
  setHoveredAlignmentPosition: (
    position: AutoLayoutAlignmentPosition | null,
  ) => void;
}) => {
  const positions = getAlignmentPositionsForGap(
    frameElement?.autoLayout?.gap || 10,
  );

  const direction = frameElement?.autoLayout?.direction || "horizontal";
  const currentPosition = frameElement?.autoLayout?.alignmentPosition;

  return (
    <div
      className="auto-layout-alignment-grid"
      onMouseLeave={() => setHoveredAlignmentPosition(null)}
    >
      {positions.map((position) => (
        <label
          key={position}
          className={currentPosition === position ? "active" : ""}
          title={`Align ${position.replace("-", " ")}`}
          onMouseEnter={() => setHoveredAlignmentPosition(position)}
        >
          <input
            type="radio"
            name="alignment"
            onChange={() =>
              autoLayoutCallbacks.handleAlignmentPositionChange(position)
            }
            checked={currentPosition === position}
            data-testid={`alignment-${position}`}
          />
          {getAlignmentIcon(
            position,
            currentPosition === position,
            hoveredAlignmentPosition === position,
            direction,
          )}
        </label>
      ))}
    </div>
  );
};

// Helper functions (duplicated from Actions.tsx)
export const canChangeStrokeColor = (
  appState: UIAppState,
  targetElements: ExcalidrawElement[],
) => {
  let commonSelectedType: ExcalidrawElement["type"] | null =
    targetElements[0]?.type || null;

  for (const element of targetElements) {
    if (element.type !== commonSelectedType) {
      commonSelectedType = null;
      break;
    }
  }

  return (
    (hasStrokeColor(appState.activeTool.type) &&
      commonSelectedType !== "image") ||
    targetElements.some((element) => hasStrokeColor(element.type))
  );
};

export const canChangeBackgroundColor = (
  appState: UIAppState,
  targetElements: ExcalidrawElement[],
) => {
  return (
    hasBackground(appState.activeTool.type) ||
    targetElements.some((element) => hasBackground(element.type))
  );
};

// Auto Layout helper functions
const updateFrameAutoLayout = (
  frame: ExcalidrawFrameElement,
  updates: Parameters<typeof updateAutoLayoutProperties>[1],
  app: AppClassProperties,
  options: { captureHistory?: boolean } = { captureHistory: true },
) => {
  // Update the frame with new auto-layout properties (e.g., direction)
  const updatedFrame = updateAutoLayoutProperties(frame, updates);

  // 1️⃣ Re-calculate layout for the updated frame first
  const allElements = app.scene.getElementsIncludingDeleted();
  const currentElementsMap = app.scene.getNonDeletedElementsMap();
  let newElements = applyAutoLayout(
    updatedFrame,
    allElements,
    currentElementsMap,
  );

  // 2️⃣ Ensure every other auto-layout frame is up-to-date as well. This is
  // important when layout changes on one frame (especially when the first
  // child is a text element) indirectly affect siblings or when cached
  // measurements are stale. By re-applying auto-layout to *all* frames we
  // guarantee an immediate visual update after property changes.
  const newElementsMap = new Map(
    newElements.filter((el) => !el.isDeleted).map((el) => [el.id, el]),
  );
  newElements = [...reapplyAutoLayoutToAllFrames(newElements, newElementsMap)];

  // 3️⃣ Commit the changes to the scene
  app.scene.replaceAllElements(newElements);

  // 4️⃣ Record the change in undo/redo history (only if requested)
  if (options.captureHistory) {
    app.syncActionResult({
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    });
  }

  // 5️⃣ Apply the same layered rerendering pattern used to fix other auto layout rerender issues
  // This is especially important for nested auto layout frames where parent frames need to reposition
  // child frames after direction changes
  setTimeout(() => {
    const currentElements = app.scene.getElementsIncludingDeleted();
    const currentElementsMap = app.scene.getNonDeletedElementsMap();
    const rerenderedElements = reapplyAutoLayoutToAllFrames(
      currentElements,
      currentElementsMap,
    );
    if (rerenderedElements !== currentElements) {
      app.scene.replaceAllElements(rerenderedElements);

      // 6️⃣ Trigger additional auto layout rerendering for nested frames
      setTimeout(() => {
        const finalElements = app.scene.getElementsIncludingDeleted();
        const finalElementsMap = app.scene.getNonDeletedElementsMap();
        const finalRerenderedElements = reapplyAutoLayoutToAllFrames(
          finalElements,
          finalElementsMap,
        );
        if (finalRerenderedElements !== finalElements) {
          app.scene.replaceAllElements(finalRerenderedElements);
        }

        // 7️⃣ Final trigger to ensure visual re-rendering
        app.scene.triggerUpdate();
      }, 0);
    } else {
      // Even if no layout changes, trigger update for visual feedback
      app.scene.triggerUpdate();
    }
  }, 0);
};

// Auto Layout drag callbacks
const createAutoLayoutCallbacks = (
  frame: ExcalidrawFrameElement,
  app: AppClassProperties,
) => ({
  handleAutoLayoutDimensionChange: () => {
    // Dimension changes are handled by the Dimension component itself
    // Auto layout will be applied when the frame is updated
  },

  handleAutoLayoutDragFinished: () => {
    // Apply auto layout after dimension changes
    if (frame.autoLayout?.enabled) {
      const allElements = app.scene.getElementsIncludingDeleted();
      const elementsMap = app.scene.getNonDeletedElementsMap();
      const newElements = reapplyAutoLayoutToAllFrames(
        allElements,
        elementsMap,
      );
      app.scene.replaceAllElements(newElements);
    }
  },

  handleAutoLayoutGapChange: (value: number) => {
    // Get the latest frame element from the scene to avoid stale closure references
    const allElements = app.scene.getElementsIncludingDeleted();
    const latestFrame = allElements.find(
      (el) => el.id === frame.id,
    ) as ExcalidrawFrameElement;

    if (!latestFrame || !isFrameLikeElement(latestFrame)) {
      return;
    }

    // During drag, don't capture history - DragInput component will handle it on drag end
    updateFrameAutoLayout(latestFrame, { gap: value }, app, { captureHistory: false });
  },

  handleAutoLayoutGapTypeChange: (gapType: AutoLayoutGap) => {
    // Get the latest frame element from the scene to avoid stale closure references
    const allElements = app.scene.getElementsIncludingDeleted();
    const latestFrame = allElements.find(
      (el) => el.id === frame.id,
    ) as ExcalidrawFrameElement;

    if (!latestFrame || !isFrameLikeElement(latestFrame)) {
      return;
    }

    updateFrameAutoLayout(latestFrame, { gap: gapType }, app);
  },

  handleAutoLayoutGapDragFinished: () => {
    // Capture history after gap drag is finished
    app.syncActionResult({
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    });
  },

  handleAutoLayoutPaddingChange: (
    side: "top" | "right" | "bottom" | "left",
    value: number,
  ) => {
    // Get the latest frame element from the scene to avoid stale closure references
    const allElements = app.scene.getElementsIncludingDeleted();
    const latestFrame = allElements.find(
      (el) => el.id === frame.id,
    ) as ExcalidrawFrameElement;

    if (!latestFrame || !isFrameLikeElement(latestFrame)) {
      return;
    }

    const currentPadding =
      latestFrame.autoLayout?.padding || DEFAULT_AUTO_LAYOUT_PROPERTIES.padding;
    // During drag, don't capture history - DragInput component will handle it on drag end
    updateFrameAutoLayout(
      latestFrame,
      { padding: { ...currentPadding, [side]: value } },
      app,
      { captureHistory: false },
    );
  },

  handleAutoLayoutPaddingDragFinished: () => {
    // Capture history after padding drag is finished
    app.syncActionResult({
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    });
  },

  handleBulkAutoLayoutPaddingChange: (padding: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  }) => {
    // Get the latest frame element from the scene to avoid stale closure references
    const allElements = app.scene.getElementsIncludingDeleted();
    const latestFrame = allElements.find(
      (el) => el.id === frame.id,
    ) as ExcalidrawFrameElement;

    if (!latestFrame || !isFrameLikeElement(latestFrame)) {
      return;
    }

    // During drag, don't capture history - DragInput component will handle it on drag end
    updateFrameAutoLayout(latestFrame, { padding }, app, { captureHistory: false });
  },

  handleDirectionChange: (direction: AutoLayoutDirection) => {
    // Get the latest frame element from the scene to avoid stale closure references
    const allElements = app.scene.getElementsIncludingDeleted();
    const latestFrame = allElements.find(
      (el) => el.id === frame.id,
    ) as ExcalidrawFrameElement;

    if (!latestFrame || !isFrameLikeElement(latestFrame)) {
      return;
    }

    // Get the current direction before updating
    const currentDirection = latestFrame.autoLayout?.direction || "vertical";

    // Only apply child resizing logic if direction is actually changing
    if (currentDirection !== direction) {
      // Apply Figma's child resizing logic when direction changes
      const elementsMap = app.scene.getNonDeletedElementsMap();
      const frameChildren = getAutoLayoutChildren(
        latestFrame,
        allElements,
        elementsMap,
      );

      // Check if parent frame has auto/hug resizing (vs fixed)
      const parentHasAutoWidth =
        latestFrame.autoLayout?.resizing?.width === "hug";
      const parentHasAutoHeight =
        latestFrame.autoLayout?.resizing?.height === "hug";

      // Update child resizing properties based on Figma logic
      const updatedChildren = frameChildren.map((child: ExcalidrawElement) => {
        const currentChildResizing = (child as any).autoLayoutChildResizing || {
          width: "fixed",
          height: "fixed",
        };

        const newChildResizing = { ...currentChildResizing };

        // Apply Figma logic based on parent frame resizing and direction change
        if (parentHasAutoWidth || parentHasAutoHeight) {
          // IF parent frame is on Auto W/H auto layout
          // When changing direction, convert any Fill resizing to Fixed on both axes
          // This prevents layout breakage when orientation changes
          if (
            currentChildResizing.width === "fill" ||
            currentChildResizing.height === "fill"
          ) {
            newChildResizing.width = "fixed";
            newChildResizing.height = "fixed";
          }
        }
        // If parent frame is on Fixed size auto layout, child resizing can stay as is

        return {
          ...child,
          autoLayoutChildResizing: newChildResizing,
        };
      });

      // Apply child resizing updates to the scene
      if (updatedChildren.length > 0) {
        app.scene.replaceAllElements(
          allElements.map((el) => {
            const updated = updatedChildren.find(
              (u: ExcalidrawElement) => u.id === el.id,
            );
            return updated || el;
          }),
        );
      }
    }

    // Update the frame direction
    updateFrameAutoLayout(latestFrame, { direction }, app);
  },

  handleAlignmentPositionChange: (position: AutoLayoutAlignmentPosition) => {
    // Get the latest frame element from the scene to avoid stale closure references
    const allElements = app.scene.getElementsIncludingDeleted();
    const latestFrame = allElements.find(
      (el) => el.id === frame.id,
    ) as ExcalidrawFrameElement;

    if (!latestFrame || !isFrameLikeElement(latestFrame)) {
      return;
    }

    updateFrameAutoLayout(latestFrame, { alignmentPosition: position }, app);
  },

  handleResizingChange: (
    dimension: "width" | "height",
    resizing: AutoLayoutResizing,
  ) => {
    // Get the latest frame element from the scene to avoid stale closure references
    const allElements = app.scene.getElementsIncludingDeleted();
    const latestFrame = allElements.find(
      (el) => el.id === frame.id,
    ) as ExcalidrawFrameElement;

    if (!latestFrame || !isFrameLikeElement(latestFrame)) {
      return;
    }

    const currentResizing =
      latestFrame.autoLayout?.resizing ||
      DEFAULT_AUTO_LAYOUT_PROPERTIES.resizing;
    updateFrameAutoLayout(
      latestFrame,
      { resizing: { ...currentResizing, [dimension]: resizing } },
      app,
    );
  },
  handleChildResizingChange: (
    elements: ExcalidrawElement[],
    dimension: "width" | "height",
    resizing: "fill" | "fixed",
  ) => {
    const updatedElements = elements.map((element) => {
      const currentChildResizing = (element as any).autoLayoutChildResizing || {
        width: "fixed",
        height: "fixed",
      };
      return {
        ...element,
        autoLayoutChildResizing: {
          ...currentChildResizing,
          [dimension]: resizing,
        },
      };
    });

    app.scene.replaceAllElements(
      app.scene.getElementsIncludingDeleted().map((el) => {
        const updated = updatedElements.find((u) => u.id === el.id);
        return updated || el;
      }),
    );

    // Record the change in undo/redo history
    app.syncActionResult({
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    });
  },
});

export const PropertiesBar = ({
  appState,
  elementsMap,
  renderAction,
  app,
}: {
  appState: UIAppState;
  elementsMap: NonDeletedElementsMap | NonDeletedSceneElementsMap;
  renderAction: ActionManager["renderAction"];
  app: AppClassProperties;
}) => {
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [showDetailedPadding, setShowDetailedPadding] = useState(false);
  const [showGapOptions, setShowGapOptions] = useState(false);
  const [hoveredAlignmentPosition, setHoveredAlignmentPosition] =
    useState<AutoLayoutAlignmentPosition | null>(null);
  const targetElements = getTargetElements(elementsMap, appState);

  let isSingleElementBoundContainer = false;
  if (
    targetElements.length === 2 &&
    (hasBoundTextElement(targetElements[0]) ||
      hasBoundTextElement(targetElements[1]))
  ) {
    isSingleElementBoundContainer = true;
  }
  const isEditingTextOrNewElement = Boolean(
    appState.editingTextElement || appState.newElement,
  );
  const device = useDevice();
  const isRTL = document.documentElement.getAttribute("dir") === "rtl";

  const showFillIcons =
    (hasBackground(appState.activeTool.type) &&
      appState.activeTool.type !== "frame" &&
      appState.activeTool.type !== "magicframe" &&
      !isTransparent(appState.currentItemBackgroundColor)) ||
    targetElements.some(
      (element) =>
        hasBackground(element.type) &&
        element.type !== "frame" &&
        element.type !== "magicframe" &&
        !isTransparent(element.backgroundColor),
    );

  const showLinkIcon =
    targetElements.length === 1 || isSingleElementBoundContainer;

  const showLineEditorAction =
    !appState.editingLinearElement &&
    targetElements.length === 1 &&
    isLinearElement(targetElements[0]) &&
    !isElbowArrow(targetElements[0]);

  const showCropEditorAction =
    !appState.croppingElementId &&
    targetElements.length === 1 &&
    isImageElement(targetElements[0]);

  const showAlignActions =
    !isSingleElementBoundContainer && alignActionsPredicate(appState, app);

  // Shape properties logic - use full AppState and proper elementsMap
  const fullAppState = useExcalidrawAppState();
  const scene = app.scene;
  const sceneElementsMap = scene.getNonDeletedElementsMap();
  const selectedElements = scene.getSelectedElements({
    selectedElementIds: appState.selectedElementIds,
    includeBoundTextElement: false,
  });

  const singleElement =
    selectedElements.length === 1 ? selectedElements[0] : null;
  const multipleElements =
    selectedElements.length > 1 ? selectedElements : null;
  const atomicUnits = getAtomicUnits(selectedElements, fullAppState);

  // Auto layout state and callbacks
  const frameElement =
    singleElement && isFrameLikeElement(singleElement)
      ? (singleElement as ExcalidrawFrameElement)
      : null;

  const autoLayoutCallbacks = frameElement
    ? createAutoLayoutCallbacks(frameElement, app)
    : null;

  // Update auto layout enabled state based on frame's actual state
  const actualAutoLayoutEnabled = frameElement?.autoLayout?.enabled || false;

  // Handle auto layout toggle
  const handleAutoLayoutToggle = (enabled: boolean) => {
    if (!frameElement) {
      return;
    }

    const allElements = app.scene.getElementsIncludingDeleted();
    const elementsMap = app.scene.getNonDeletedElementsMap();

    // Compute the updated frame with the new enabled state
    let updatedFrame = toggleAutoLayout(
      frameElement,
      enabled,
      enabled ? allElements : undefined,
      enabled ? elementsMap : undefined,
    );

    // Auto-hide frame title when enabling auto layout
    if (enabled) {
      updatedFrame = {
        ...updatedFrame,
        showName: false,
      } as typeof updatedFrame;
    }

    let newElements: ExcalidrawElement[];

    if (enabled) {
      // When enabling, apply auto-layout to update both frame and children
      newElements = applyAutoLayout(updatedFrame, allElements, elementsMap);

      // Force immediate rerendering to ensure auto layout is applied immediately
      app.scene.replaceAllElements(newElements);

      // Record the change in undo/redo history
      app.syncActionResult({
        captureUpdate: CaptureUpdateAction.IMMEDIATELY,
      });

      // Trigger additional auto layout rerendering to ensure all nested elements are positioned correctly
      setTimeout(() => {
        const currentElements = app.scene.getElementsIncludingDeleted();
        const currentElementsMap = app.scene.getNonDeletedElementsMap();
        const rerenderedElements = reapplyAutoLayoutToAllFrames(
          currentElements,
          currentElementsMap,
        );
        if (rerenderedElements !== currentElements) {
          app.scene.replaceAllElements(rerenderedElements);
        }
        
        // Explicitly recalculate all bound text positions to fix grandchild text positioning
        setTimeout(() => {
          const finalElements = app.scene.getElementsIncludingDeleted();
          const finalElementsMap = app.scene.getNonDeletedElementsMap();
          const elementsWithCorrectTextPositions = recalculateAllBoundTextPositions(
            finalElements,
            finalElementsMap,
          );
          
          if (elementsWithCorrectTextPositions !== finalElements) {
            app.scene.replaceAllElements(elementsWithCorrectTextPositions);
          }
          
          // Final trigger to ensure visual feedback for text positioning even when no layout changes occur
          app.scene.triggerUpdate();
        }, 0);
      }, 0);
    } else {
      // When disabling, simply replace the frame in the element list
      newElements = allElements.map((el) =>
        el.id === updatedFrame.id ? updatedFrame : el,
      );
      app.scene.replaceAllElements(newElements);

      // Record the change in undo/redo history
      app.syncActionResult({
        captureUpdate: CaptureUpdateAction.IMMEDIATELY,
      });
    }
  };

  // Set up auto layout listener for automatic reapplication when elements change
  useEffect(() => {
    const removeListener = setupAutoLayoutListener(app.scene);
    return removeListener;
  }, [app.scene]);

  return (
    <div className="properties-bar">
      {/* Position Section */}
      {selectedElements.length > 0 && (
        <PropertySection legend="Position">
          <div className="shape-properties">
            {singleElement && (
              <>
                {/* X and Y position on one line */}
                <div className="property-row-dual">
                  <div className="property-item">
                    <Position
                      element={singleElement}
                      property="x"
                      elementsMap={sceneElementsMap}
                      scene={scene}
                      appState={fullAppState}
                    />
                  </div>
                  <div className="property-item">
                    <Position
                      element={singleElement}
                      property="y"
                      elementsMap={sceneElementsMap}
                      scene={scene}
                      appState={fullAppState}
                    />
                  </div>
                </div>

                {/* Width and Height on one line */}
                <div className="property-row-dual">
                  <div className="property-item">
                    <Dimension
                      property="width"
                      element={singleElement}
                      scene={scene}
                      appState={fullAppState}
                    />
                  </div>
                  <div className="property-item">
                    <Dimension
                      property="height"
                      element={singleElement}
                      scene={scene}
                      appState={fullAppState}
                    />
                  </div>
                </div>

                {/* Rotation and Opacity on one line */}
                {!isElbowArrow(singleElement) && (
                  <div className="property-row-dual">
                    <div className="property-item">
                      <Angle
                        property="angle"
                        element={singleElement}
                        scene={scene}
                        appState={fullAppState}
                      />
                    </div>
                    <div className="property-item">
                      {renderAction("changeOpacity")}
                    </div>
                  </div>
                )}
              </>
            )}

            {multipleElements && (
              <>
                {/* X and Y position on one line */}
                <div className="property-row-dual">
                  <div className="property-item">
                    <MultiPosition
                      property="x"
                      elements={multipleElements}
                      elementsMap={sceneElementsMap}
                      atomicUnits={atomicUnits}
                      scene={scene}
                      appState={fullAppState}
                    />
                  </div>
                  <div className="property-item">
                    <MultiPosition
                      property="y"
                      elements={multipleElements}
                      elementsMap={sceneElementsMap}
                      atomicUnits={atomicUnits}
                      scene={scene}
                      appState={fullAppState}
                    />
                  </div>
                </div>

                {/* Width and Height on one line */}
                <div className="property-row-dual">
                  <div className="property-item">
                    <MultiDimension
                      property="width"
                      elements={multipleElements}
                      elementsMap={sceneElementsMap}
                      atomicUnits={atomicUnits}
                      scene={scene}
                      appState={fullAppState}
                    />
                  </div>
                  <div className="property-item">
                    <MultiDimension
                      property="height"
                      elements={multipleElements}
                      elementsMap={sceneElementsMap}
                      atomicUnits={atomicUnits}
                      scene={scene}
                      appState={fullAppState}
                    />
                  </div>
                </div>

                {/* Rotation and Opacity on one line */}
                <div className="property-row-dual">
                  <div className="property-item">
                    <MultiAngle
                      property="angle"
                      elements={multipleElements}
                      scene={scene}
                      appState={fullAppState}
                    />
                  </div>
                  <div className="property-item">
                    {renderAction("changeOpacity")}
                  </div>
                </div>
              </>
            )}

            {/* Align Objects - moved into Position section */}
            {showAlignActions &&
              !isSingleElementBoundContainer &&
              // Hide alignment options if selected objects are children of auto layout frames
              // but allow alignment when frames themselves are selected
              !selectedElements.some((el) => {
                // If the selected element is a frame itself, allow alignment
                if (isFrameLikeElement(el)) {
                  return false;
                }
                // If the selected element is a child of an auto layout frame, hide alignment
                if (!el.frameId) {
                  return false;
                }
                const frame = elementsMap.get(el.frameId);
                return (
                  frame &&
                  isFrameLikeElement(frame) &&
                  (frame as ExcalidrawFrameElement).autoLayout?.enabled
                );
              }) && (
                <fieldset style={{ marginTop: "0rem" }}>
                  <legend>{t("labels.alignment")}</legend>

                  {/* 2-column layout for Horizontal and Vertical alignment */}
                  <div
                    className="alignment-section-dual"
                    style={{ marginBottom: "4px" }}
                  >
                    {/* Horizontal alignment column */}
                    <div className="alignment-column">
                      <div
                        className={`buttonList alignment-horizontal-buttons ${
                          targetElements.length > 2 ? "has-distribute" : ""
                        }`}
                      >
                        {
                          // swap this order for RTL so the button positions always match their action
                          // (i.e. the leftmost button aligns left)
                        }
                        {isRTL ? (
                          <>
                            {renderAction("alignRight")}
                            {renderAction("alignHorizontallyCentered")}
                            {renderAction("alignLeft")}
                          </>
                        ) : (
                          <>
                            {renderAction("alignLeft")}
                            {renderAction("alignHorizontallyCentered")}
                            {renderAction("alignRight")}
                          </>
                        )}
                        {targetElements.length > 2 &&
                          renderAction("distributeHorizontally")}
                      </div>
                    </div>

                    {/* Vertical alignment column */}
                    <div className="alignment-column">
                      <div
                        className={`buttonList alignment-vertical-buttons ${
                          targetElements.length > 2 ? "has-distribute" : ""
                        }`}
                      >
                        {renderAction("alignTop")}
                        {renderAction("alignVerticallyCentered")}
                        {renderAction("alignBottom")}
                        {targetElements.length > 2 &&
                          renderAction("distributeVertically")}
                      </div>
                    </div>
                  </div>
                </fieldset>
              )}

            {/* Spacing Section - Figma-like spacing between objects */}
            {selectedElements.length > 1 && (
              <fieldset>
                <legend>{t("labels.spacing")}</legend>
                <Spacing
                  elements={selectedElements}
                  elementsMap={scene.getNonDeletedElementsMap() as any}
                  scene={scene}
                  appState={fullAppState}
                  property="spacing"
                />
              </fieldset>
            )}

            {(canChangeRoundness(appState.activeTool.type) ||
              targetElements.some((element) =>
                canChangeRoundness(element.type),
              )) && <>{renderAction("changeRoundness")}</>}
          </div>
        </PropertySection>
      )}

      {/* Auto Layout Child Properties - shown when elements inside auto layout frames are selected */}
      {(() => {
        // Check if any selected elements are children of auto layout frames
        const elementsInAutoLayout = targetElements.filter((el) => {
          if (!el.frameId) {
            return false;
          }
          const frame = elementsMap.get(el.frameId);
          return (
            frame &&
            isFrameLikeElement(frame) &&
            (frame as ExcalidrawFrameElement).autoLayout?.enabled
          );
        });

        if (elementsInAutoLayout.length === 0) {
          return null;
        }

        // Don't show this section if we have a single frame element (it will get its own Auto Layout Section)
        if (singleElement && isFrameLikeElement(singleElement)) {
          return null;
        }

        // Get the auto-layout frame
        const frameId = elementsInAutoLayout[0].frameId!;
        const autoLayoutFrame = elementsMap.get(
          frameId,
        ) as ExcalidrawFrameElement;

        if (!autoLayoutFrame || !autoLayoutFrame.autoLayout?.enabled) {
          return null;
        }

        // Check if we have a single frame element without auto layout
        const isSingleNestedFrameWithoutAutoLayout =
          singleElement &&
          isFrameLikeElement(singleElement) &&
          !(singleElement as ExcalidrawFrameElement).autoLayout?.enabled &&
          elementsInAutoLayout.includes(singleElement);

        // Check if we have a single nested frame WITH auto layout (this will get move buttons in its own section)
        const isSingleNestedFrameWithAutoLayout =
          singleElement &&
          isFrameLikeElement(singleElement) &&
          (singleElement as ExcalidrawFrameElement).autoLayout?.enabled &&
          elementsInAutoLayout.includes(singleElement);

        // Create child resizing callbacks
        const handleChildResizingChange = (
          elements: ExcalidrawElement[],
          dimension: "width" | "height",
          resizing: "fill" | "fixed",
        ) => {
          const updatedElements = elements.map((element) => {
            const currentChildResizing = (element as any)
              .autoLayoutChildResizing || {
              width: "fixed",
              height: "fixed",
            };
            return {
              ...element,
              autoLayoutChildResizing: {
                ...currentChildResizing,
                [dimension]: resizing,
              },
            };
          });

          // Update elements and trigger auto layout recalculation
          const allElements = app.scene.getElementsIncludingDeleted();
          const newElements = allElements.map((el) => {
            const updated = updatedElements.find((u) => u.id === el.id);
            return updated || el;
          });

          // Apply auto layout to all frames after updating child properties
          const elementsMap = app.scene.getNonDeletedElementsMap();
          const finalElements = reapplyAutoLayoutToAllFrames(
            newElements,
            elementsMap,
          );

          app.scene.replaceAllElements(finalElements);

          // Record the change in undo/redo history
          app.syncActionResult({
            captureUpdate: CaptureUpdateAction.IMMEDIATELY,
          });
        };

        const isHorizontal =
          autoLayoutFrame.autoLayout.direction === "horizontal";

        const handleReorder = (
          operation: "first" | "last" | "previous" | "next",
        ) => {
          const allElements = app.scene.getElementsIncludingDeleted();
          const newElements = reorderAutoLayoutElements(
            autoLayoutFrame,
            elementsInAutoLayout,
            operation,
            allElements,
            app.scene.getNonDeletedElementsMap(),
          );
          app.scene.replaceAllElements(newElements);

          // Record the change in undo/redo history
          app.syncActionResult({
            captureUpdate: CaptureUpdateAction.IMMEDIATELY,
          });

          // Trigger additional auto layout rerendering to ensure all layout (padding, gaps) is properly applied
          setTimeout(() => {
            const currentElements = app.scene.getElementsIncludingDeleted();
            const currentElementsMap = app.scene.getNonDeletedElementsMap();
            const rerenderedElements = reapplyAutoLayoutToAllFrames(
              currentElements,
              currentElementsMap,
            );
            if (rerenderedElements !== currentElements) {
              app.scene.replaceAllElements(rerenderedElements);
            }

            // Final trigger to ensure visual re-rendering after auto-layout changes
            app.scene.triggerUpdate();
          }, 0);
        };

        return (
          <PropertySection legend="Auto layout" showSeparator={true}>
            <div className="auto-layout-child-properties">
              {/* Show child resizing for non-auto-layout elements (including nested frames without auto layout) */}
              {(elementsInAutoLayout.some((el) => !isFrameLikeElement(el)) ||
                isSingleNestedFrameWithoutAutoLayout) && (
                <fieldset className="auto-layout-section">
                  <div className="auto-layout-resizing">
                    {/* Width resizing for child elements */}
                    <div className="auto-layout-resize-section">
                      <label className="auto-layout-resize-label">Width</label>
                      <div className="auto-layout-resize-buttons">
                        <button
                          type="button"
                          className={`auto-layout-resize-option ${
                            elementsInAutoLayout.every(
                              (el) =>
                                (el as any).autoLayoutChildResizing?.width !==
                                "fill",
                            )
                              ? "active"
                              : ""
                          }`}
                          onClick={() => {
                            handleChildResizingChange(
                              elementsInAutoLayout,
                              "width",
                              "fixed",
                            );
                          }}
                          title="Fixed width - Element maintains its current width"
                        >
                          Fixed
                        </button>
                        <button
                          type="button"
                          className={`auto-layout-resize-option ${
                            elementsInAutoLayout.every(
                              (el) =>
                                (el as any).autoLayoutChildResizing?.width ===
                                "fill",
                            )
                              ? "active"
                              : ""
                          }`}
                          onClick={() => {
                            handleChildResizingChange(
                              elementsInAutoLayout,
                              "width",
                              "fill",
                            );
                          }}
                          title="Fill container - Element stretches to fill available width"
                        >
                          Fill
                        </button>
                      </div>
                    </div>

                    {/* Height resizing for child elements */}
                    <div className="auto-layout-resize-section">
                      <label className="auto-layout-resize-label">Height</label>
                      <div className="auto-layout-resize-buttons">
                        <button
                          type="button"
                          className={`auto-layout-resize-option ${
                            elementsInAutoLayout.every(
                              (el) =>
                                (el as any).autoLayoutChildResizing?.height !==
                                "fill",
                            )
                              ? "active"
                              : ""
                          }`}
                          onClick={() => {
                            handleChildResizingChange(
                              elementsInAutoLayout,
                              "height",
                              "fixed",
                            );
                          }}
                          title="Fixed height - Element maintains its current height"
                        >
                          Fixed
                        </button>
                        <button
                          type="button"
                          className={`auto-layout-resize-option ${
                            elementsInAutoLayout.every(
                              (el) =>
                                (el as any).autoLayoutChildResizing?.height ===
                                "fill",
                            )
                              ? "active"
                              : ""
                          }`}
                          onClick={() => {
                            handleChildResizingChange(
                              elementsInAutoLayout,
                              "height",
                              "fill",
                            );
                          }}
                          title="Fill container - Element stretches to fill available height"
                        >
                          Fill
                        </button>
                      </div>
                    </div>
                  </div>
                </fieldset>
              )}

              {/* Move position section - show for elements in auto layout, but NOT for nested frames with auto layout (they get it in their own section) */}
              {!isSingleNestedFrameWithAutoLayout && (
                <fieldset className="auto-layout-section">
                  <legend>Move position</legend>
                  <div className="auto-layout-order-buttons">
                    <button
                      type="button"
                      className="auto-layout-order-button"
                      onClick={() => handleReorder("first")}
                      title={
                        isHorizontal
                          ? "Move to leftmost position"
                          : "Move to top position"
                      }
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        {isHorizontal ? (
                          // Horizontal: Move to left
                          <>
                            <rect
                              x="2"
                              y="4"
                              width="2"
                              height="8"
                              fill="currentColor"
                            />
                            <path d="M6 8L10 4V12L6 8Z" fill="currentColor" />
                          </>
                        ) : (
                          // Vertical: Move to top
                          <>
                            <rect
                              x="4"
                              y="2"
                              width="8"
                              height="2"
                              fill="currentColor"
                            />
                            <path d="M8 6L4 10H12L8 6Z" fill="currentColor" />
                          </>
                        )}
                      </svg>
                      First
                    </button>

                    <button
                      type="button"
                      className="auto-layout-order-button"
                      onClick={() => handleReorder("previous")}
                      title={isHorizontal ? "Move left" : "Move up"}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        {isHorizontal ? (
                          // Left arrow
                          <path
                            d="M10 4L6 8L10 12"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        ) : (
                          // Up arrow
                          <path
                            d="M4 10L8 6L12 10"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        )}
                      </svg>
                      {isHorizontal ? "Left" : "Up"}
                    </button>

                    <button
                      type="button"
                      className="auto-layout-order-button"
                      onClick={() => handleReorder("next")}
                      title={isHorizontal ? "Move right" : "Move down"}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        {isHorizontal ? (
                          // Right arrow
                          <path
                            d="M6 4L10 8L6 12"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        ) : (
                          // Down arrow
                          <path
                            d="M12 6L8 10L4 6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        )}
                      </svg>
                      {isHorizontal ? "Right" : "Down"}
                    </button>

                    <button
                      type="button"
                      className="auto-layout-order-button"
                      onClick={() => handleReorder("last")}
                      title={
                        isHorizontal
                          ? "Move to rightmost position"
                          : "Move to bottom position"
                      }
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        {isHorizontal ? (
                          // Horizontal: Move to right
                          <>
                            <rect
                              x="12"
                              y="4"
                              width="2"
                              height="8"
                              fill="currentColor"
                            />
                            <path d="M10 8L6 4V12L10 8Z" fill="currentColor" />
                          </>
                        ) : (
                          // Vertical: Move to bottom
                          <>
                            <rect
                              x="4"
                              y="12"
                              width="8"
                              height="2"
                              fill="currentColor"
                            />
                            <path d="M8 10L4 6H12L8 10Z" fill="currentColor" />
                          </>
                        )}
                      </svg>
                      Last
                    </button>
                  </div>
                </fieldset>
              )}
            </div>
          </PropertySection>
        );
      })()}

      {/* Auto Layout Section */}
      {singleElement && isFrameLikeElement(singleElement) && (
        <PropertySection>
          <div className="shape-properties auto-layout-properties">
            {/* Show frame header toggle */}
            <div className="auto-layout-header" style={{ marginBottom: 4 }}>
              <span className="auto-layout-legend">Show frame header</span>
              <Switch
                name="frame-show-name"
                checked={frameElement!.showName !== false}
                onChange={(checked) => {
                  if (frameElement) {
                    app.scene.mutateElement(frameElement, {
                      showName: checked,
                    });
                  }
                }}
                title={
                  frameElement?.showName === false
                    ? "Show frame header"
                    : "Hide frame header"
                }
              />
            </div>
            {/* Frame header input hidden per user request (keep for future use)
            {frameElement?.showName !== false && (
              <div
                className="drag-input-container header-input"
                style={{ marginBottom: 8 }}
              >
                <input
                  className="drag-input"
                  type="text"
                  placeholder="Header text"
                  value={frameElement!.name ?? ""}
                  onChange={(e) => {
                    if (frameElement) {
                      app.scene.mutateElement(frameElement, {
                        name: e.target.value,
                      });
                    }
                  }}
                  style={{ width: "100%" }}
                />
              </div>
            )}
            */}
            {/* Auto Layout Header with Toggle */}
            <div className="auto-layout-header" style={{ marginBottom: 0 }}>
              <span className="auto-layout-legend">Auto layout</span>
              <Switch
                name="auto-layout-toggle"
                checked={actualAutoLayoutEnabled}
                onChange={handleAutoLayoutToggle}
                title="Toggle auto layout"
              />
            </div>

            {/* Non-Auto Layout Frame Properties - show when auto layout is disabled AND frame is child of auto layout */}
            {!actualAutoLayoutEnabled &&
              frameElement &&
              frameElement.frameId &&
              (() => {
                // Only show Fixed|Fill buttons when frame is nested inside an auto layout frame
                const parentFrame = elementsMap.get(frameElement.frameId);
                return (
                  parentFrame &&
                  isFrameLikeElement(parentFrame) &&
                  (parentFrame as ExcalidrawFrameElement).autoLayout?.enabled
                );
              })() && (
                <>
                  {/* Fixed|Fill buttons for Width and Height */}
                  <div
                    className="alignment-section-dual"
                    style={{ marginBottom: "1rem", marginTop: "8px" }}
                  >
                    {/* Width Column */}
                    <div className="alignment-column">
                      <fieldset className="auto-layout-section">
                        <legend>Width</legend>
                        <div className="auto-layout-resize-buttons">
                          <button
                            type="button"
                            className={`auto-layout-resize-option ${
                              (frameElement as any).autoLayoutChildResizing
                                ?.width !== "fill"
                                ? "active"
                                : ""
                            }`}
                            onClick={() => {
                              // Set to fixed mode - clear fill resizing
                              const allElements =
                                app.scene.getElementsIncludingDeleted();
                              const updatedElements = allElements.map((el) => {
                                if (el.id === frameElement.id) {
                                  const currentChildResizing = (
                                    frameElement as any
                                  ).autoLayoutChildResizing;
                                  const newChildResizing =
                                    currentChildResizing?.height === "fill"
                                      ? { height: "fill" as const }
                                      : undefined;
                                  return {
                                    ...el,
                                    autoLayoutChildResizing: newChildResizing,
                                  };
                                }
                                return el;
                              });

                              // Apply auto layout to parent frames if this frame is in an auto layout
                              const elementsMap =
                                app.scene.getNonDeletedElementsMap();
                              const finalElements =
                                reapplyAutoLayoutToAllFrames(
                                  updatedElements,
                                  elementsMap,
                                );
                              app.scene.replaceAllElements(finalElements);
                              app.scene.triggerUpdate();
                            }}
                            title="Fixed width - Frame maintains its current width"
                          >
                            Fixed
                          </button>
                          {/* Show Fill option only when frame is nested inside an auto layout frame */}
                          {frameElement.frameId &&
                            (() => {
                              const parentFrame = elementsMap.get(
                                frameElement.frameId,
                              );
                              return (
                                parentFrame &&
                                isFrameLikeElement(parentFrame) &&
                                (parentFrame as ExcalidrawFrameElement)
                                  .autoLayout?.enabled
                              );
                            })() && (
                              <button
                                type="button"
                                className={`auto-layout-resize-option ${
                                  (frameElement as any).autoLayoutChildResizing
                                    ?.width === "fill"
                                    ? "active"
                                    : ""
                                }`}
                                onClick={() => {
                                  // Set to fill mode
                                  const allElements =
                                    app.scene.getElementsIncludingDeleted();
                                  const updatedElements = allElements.map(
                                    (el) => {
                                      if (el.id === frameElement.id) {
                                        const currentChildResizing =
                                          (frameElement as any)
                                            .autoLayoutChildResizing || {};
                                        return {
                                          ...el,
                                          autoLayoutChildResizing: {
                                            ...currentChildResizing,
                                            width: "fill" as const,
                                          },
                                        };
                                      }
                                      return el;
                                    },
                                  );

                                  // Apply auto layout to parent frames
                                  const elementsMap =
                                    app.scene.getNonDeletedElementsMap();
                                  const finalElements =
                                    reapplyAutoLayoutToAllFrames(
                                      updatedElements,
                                      elementsMap,
                                    );
                                  app.scene.replaceAllElements(finalElements);
                                  app.scene.triggerUpdate();
                                }}
                                title="Fill container - Frame stretches to fill available width"
                              >
                                Fill
                              </button>
                            )}
                        </div>
                      </fieldset>
                    </div>

                    {/* Height Column */}
                    <div className="alignment-column">
                      <fieldset className="auto-layout-section">
                        <legend>Height</legend>
                        <div className="auto-layout-resize-buttons">
                          <button
                            type="button"
                            className={`auto-layout-resize-option ${
                              (frameElement as any).autoLayoutChildResizing
                                ?.height !== "fill"
                                ? "active"
                                : ""
                            }`}
                            onClick={() => {
                              // Set to fixed mode - clear fill resizing
                              const allElements =
                                app.scene.getElementsIncludingDeleted();
                              const updatedElements = allElements.map((el) => {
                                if (el.id === frameElement.id) {
                                  const currentChildResizing = (
                                    frameElement as any
                                  ).autoLayoutChildResizing;
                                  const newChildResizing =
                                    currentChildResizing?.width === "fill"
                                      ? { width: "fill" as const }
                                      : undefined;
                                  return {
                                    ...el,
                                    autoLayoutChildResizing: newChildResizing,
                                  };
                                }
                                return el;
                              });

                              // Apply auto layout to parent frames if this frame is in an auto layout
                              const elementsMap =
                                app.scene.getNonDeletedElementsMap();
                              const finalElements =
                                reapplyAutoLayoutToAllFrames(
                                  updatedElements,
                                  elementsMap,
                                );
                              app.scene.replaceAllElements(finalElements);
                              app.scene.triggerUpdate();
                            }}
                            title="Fixed height - Frame maintains its current height"
                          >
                            Fixed
                          </button>
                          {/* Show Fill option only when frame is nested inside an auto layout frame */}
                          {frameElement.frameId &&
                            (() => {
                              const parentFrame = elementsMap.get(
                                frameElement.frameId,
                              );
                              return (
                                parentFrame &&
                                isFrameLikeElement(parentFrame) &&
                                (parentFrame as ExcalidrawFrameElement)
                                  .autoLayout?.enabled
                              );
                            })() && (
                              <button
                                type="button"
                                className={`auto-layout-resize-option ${
                                  (frameElement as any).autoLayoutChildResizing
                                    ?.height === "fill"
                                    ? "active"
                                    : ""
                                }`}
                                onClick={() => {
                                  // Set to fill mode
                                  const allElements =
                                    app.scene.getElementsIncludingDeleted();
                                  const updatedElements = allElements.map(
                                    (el) => {
                                      if (el.id === frameElement.id) {
                                        const currentChildResizing =
                                          (frameElement as any)
                                            .autoLayoutChildResizing || {};
                                        return {
                                          ...el,
                                          autoLayoutChildResizing: {
                                            ...currentChildResizing,
                                            height: "fill" as const,
                                          },
                                        };
                                      }
                                      return el;
                                    },
                                  );

                                  // Apply auto layout to parent frames
                                  const elementsMap =
                                    app.scene.getNonDeletedElementsMap();
                                  const finalElements =
                                    reapplyAutoLayoutToAllFrames(
                                      updatedElements,
                                      elementsMap,
                                    );
                                  app.scene.replaceAllElements(finalElements);
                                  app.scene.triggerUpdate();
                                }}
                                title="Fill container - Frame stretches to fill available height"
                              >
                                Fill
                              </button>
                            )}
                        </div>
                      </fieldset>
                    </div>
                  </div>

                  {/* Move Position buttons for non-auto layout frames that are children of auto layout frames */}
                  {frameElement.frameId &&
                    (() => {
                      // Check if this frame is a child of an auto layout frame
                      const parentFrame = elementsMap.get(frameElement.frameId);
                      if (
                        !parentFrame ||
                        !isFrameLikeElement(parentFrame) ||
                        !(parentFrame as ExcalidrawFrameElement).autoLayout
                          ?.enabled
                      ) {
                        return null;
                      }

                      const parentAutoLayoutFrame =
                        parentFrame as ExcalidrawFrameElement;
                      if (!parentAutoLayoutFrame.autoLayout) {
                        return null;
                      }
                      const isHorizontal =
                        parentAutoLayoutFrame.autoLayout.direction ===
                        "horizontal";

                      const handleReorder = (
                        operation: "first" | "last" | "previous" | "next",
                      ) => {
                        const allElements =
                          app.scene.getElementsIncludingDeleted();
                        const newElements = reorderAutoLayoutElements(
                          parentAutoLayoutFrame,
                          [frameElement],
                          operation,
                          allElements,
                          app.scene.getNonDeletedElementsMap(),
                        );
                        app.scene.replaceAllElements(newElements);

                        // Record the change in undo/redo history
                        app.syncActionResult({
                          captureUpdate: CaptureUpdateAction.IMMEDIATELY,
                        });

                        // Trigger additional auto layout rerendering to ensure all layout (padding, gaps) is properly applied
                        setTimeout(() => {
                          const currentElements =
                            app.scene.getElementsIncludingDeleted();
                          const currentElementsMap =
                            app.scene.getNonDeletedElementsMap();
                          const rerenderedElements =
                            reapplyAutoLayoutToAllFrames(
                              currentElements,
                              currentElementsMap,
                            );
                          if (rerenderedElements !== currentElements) {
                            app.scene.replaceAllElements(rerenderedElements);
                          }

                          // Final trigger to ensure visual re-rendering after auto-layout changes
                          app.scene.triggerUpdate();
                        }, 0);
                      };

                      return (
                        <fieldset
                          className="auto-layout-section"
                          style={{ margin: "4px 0 0 0" }}
                        >
                          <legend>Move position</legend>
                          <div className="auto-layout-order-buttons">
                            <button
                              type="button"
                              className="auto-layout-order-button"
                              onClick={() => handleReorder("first")}
                              title={
                                isHorizontal
                                  ? "Move to leftmost position"
                                  : "Move to top position"
                              }
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                {isHorizontal ? (
                                  // Horizontal: Move to left
                                  <>
                                    <rect
                                      x="2"
                                      y="4"
                                      width="2"
                                      height="8"
                                      fill="currentColor"
                                    />
                                    <path
                                      d="M6 8L10 4V12L6 8Z"
                                      fill="currentColor"
                                    />
                                  </>
                                ) : (
                                  // Vertical: Move to top
                                  <>
                                    <rect
                                      x="4"
                                      y="2"
                                      width="8"
                                      height="2"
                                      fill="currentColor"
                                    />
                                    <path
                                      d="M8 6L4 10H12L8 6Z"
                                      fill="currentColor"
                                    />
                                  </>
                                )}
                              </svg>
                              First
                            </button>

                            <button
                              type="button"
                              className="auto-layout-order-button"
                              onClick={() => handleReorder("previous")}
                              title={isHorizontal ? "Move left" : "Move up"}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                {isHorizontal ? (
                                  // Left arrow
                                  <path
                                    d="M10 4L6 8L10 12"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                ) : (
                                  // Up arrow
                                  <path
                                    d="M4 10L8 6L12 10"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                )}
                              </svg>
                              {isHorizontal ? "Left" : "Up"}
                            </button>

                            <button
                              type="button"
                              className="auto-layout-order-button"
                              onClick={() => handleReorder("next")}
                              title={isHorizontal ? "Move right" : "Move down"}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                {isHorizontal ? (
                                  // Right arrow
                                  <path
                                    d="M6 4L10 8L6 12"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                ) : (
                                  // Down arrow
                                  <path
                                    d="M12 6L8 10L4 6"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                )}
                              </svg>
                              {isHorizontal ? "Right" : "Down"}
                            </button>

                            <button
                              type="button"
                              className="auto-layout-order-button"
                              onClick={() => handleReorder("last")}
                              title={
                                isHorizontal
                                  ? "Move to rightmost position"
                                  : "Move to bottom position"
                              }
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                {isHorizontal ? (
                                  // Horizontal: Move to right
                                  <>
                                    <rect
                                      x="12"
                                      y="4"
                                      width="2"
                                      height="8"
                                      fill="currentColor"
                                    />
                                    <path
                                      d="M10 8L6 4V12L10 8Z"
                                      fill="currentColor"
                                    />
                                  </>
                                ) : (
                                  // Vertical: Move to bottom
                                  <>
                                    <rect
                                      x="4"
                                      y="12"
                                      width="8"
                                      height="2"
                                      fill="currentColor"
                                    />
                                    <path
                                      d="M8 10L4 6H12L8 10Z"
                                      fill="currentColor"
                                    />
                                  </>
                                )}
                              </svg>
                              Last
                            </button>
                          </div>
                        </fieldset>
                      );
                    })()}
                </>
              )}

            {/* Auto Layout Options - only show when enabled */}
            {actualAutoLayoutEnabled && autoLayoutCallbacks && (
              <>
                {/* Flow Direction - Directly under Auto Layout toggle */}
                <fieldset
                  className="auto-layout-section"
                  style={{ marginBottom: "8px" }}
                >
                  <legend>Flow</legend>
                  <div className="auto-layout-flow-buttons">
                    {/* Horizontal first */}
                    <button
                      type="button"
                      className={`auto-layout-flow-button ${
                        frameElement?.autoLayout?.direction === "horizontal"
                          ? "active"
                          : ""
                      }`}
                      title="Horizontal"
                      onClick={() => {
                        if (autoLayoutCallbacks) {
                          autoLayoutCallbacks.handleDirectionChange(
                            "horizontal",
                          );
                        }
                      }}
                    >
                      {autoLayoutHorizontalIcon}
                    </button>
                    {/* Vertical */}
                    <button
                      type="button"
                      className={`auto-layout-flow-button ${
                        frameElement?.autoLayout?.direction === "vertical"
                          ? "active"
                          : ""
                      }`}
                      title="Vertical"
                      onClick={() => {
                        if (autoLayoutCallbacks) {
                          autoLayoutCallbacks.handleDirectionChange("vertical");
                        }
                      }}
                    >
                      {autoLayoutVerticalIcon}
                    </button>
                  </div>
                </fieldset>

                <div
                  className="alignment-section-dual"
                  style={{ marginBottom: "1rem" }}
                >
                  {/* Left Column: Width & Height */}
                  <div className="alignment-column">
                    {/* Resizing controls */}
                    <fieldset
                      className="auto-layout-section"
                      style={{ marginBottom: ".88rem" }}
                    >
                      <legend>Width</legend>
                      <div className="auto-layout-resize-buttons">
                        <button
                          type="button"
                          className={`auto-layout-resize-option ${
                            (frameElement as any).autoLayoutChildResizing
                              ?.width === "fill"
                              ? ""
                              : frameElement?.autoLayout?.resizing.width ===
                                "hug"
                              ? "active"
                              : ""
                          }`}
                          onClick={() => {
                            if (autoLayoutCallbacks && frameElement) {
                              // Get the latest frame element from the scene to avoid stale closure references
                              const allElements =
                                app.scene.getElementsIncludingDeleted();
                              const latestFrame = allElements.find(
                                (el) => el.id === frameElement.id,
                              ) as ExcalidrawFrameElement;

                              if (
                                !latestFrame ||
                                !isFrameLikeElement(latestFrame)
                              ) {
                                return;
                              }

                              // If frame is currently in fill mode, clear the child resizing property first
                              if (
                                (frameElement as any).autoLayoutChildResizing
                                  ?.width === "fill"
                              ) {
                                const updatedElements = allElements.map(
                                  (el) => {
                                    if (el.id === frameElement.id) {
                                      return {
                                        ...el,
                                        autoLayoutChildResizing: {
                                          width: "fixed" as const,
                                          height:
                                            (frameElement as any)
                                              .autoLayoutChildResizing
                                              ?.height || "fixed",
                                        },
                                      };
                                    }
                                    return el;
                                  },
                                );

                                // Apply auto layout to all frames after clearing fill state
                                const elementsMap =
                                  app.scene.getNonDeletedElementsMap();
                                const finalElements =
                                  reapplyAutoLayoutToAllFrames(
                                    updatedElements,
                                    elementsMap,
                                  );
                                app.scene.replaceAllElements(finalElements);
                                app.scene.triggerUpdate();
                              }

                              // Use the callback function for consistency with other auto layout changes
                              autoLayoutCallbacks.handleResizingChange(
                                "width",
                                "hug",
                              );
                            }
                          }}
                          title="Hug contents - Frame resizes to fit children"
                        >
                          Hug
                        </button>
                        <button
                          type="button"
                          className={`auto-layout-resize-option ${
                            (frameElement as any).autoLayoutChildResizing
                              ?.width === "fill"
                              ? ""
                              : frameElement?.autoLayout?.resizing.width ===
                                "fixed"
                              ? "active"
                              : ""
                          }`}
                          onClick={() => {
                            if (autoLayoutCallbacks && frameElement) {
                              // Get the latest frame element from the scene to avoid stale closure references
                              const allElements =
                                app.scene.getElementsIncludingDeleted();
                              const latestFrame = allElements.find(
                                (el) => el.id === frameElement.id,
                              ) as ExcalidrawFrameElement;

                              if (
                                !latestFrame ||
                                !isFrameLikeElement(latestFrame)
                              ) {
                                return;
                              }

                              // If frame is currently in fill mode, clear the child resizing property first
                              if (
                                (frameElement as any).autoLayoutChildResizing
                                  ?.width === "fill"
                              ) {
                                const updatedElements = allElements.map(
                                  (el) => {
                                    if (el.id === frameElement.id) {
                                      return {
                                        ...el,
                                        autoLayoutChildResizing: {
                                          width: "fixed" as const,
                                          height:
                                            (frameElement as any)
                                              .autoLayoutChildResizing
                                              ?.height || "fixed",
                                        },
                                      };
                                    }
                                    return el;
                                  },
                                );

                                // Apply auto layout to all frames after clearing fill state
                                const elementsMap =
                                  app.scene.getNonDeletedElementsMap();
                                const finalElements =
                                  reapplyAutoLayoutToAllFrames(
                                    updatedElements,
                                    elementsMap,
                                  );
                                app.scene.replaceAllElements(finalElements);
                                app.scene.triggerUpdate();
                              }

                              // Use the callback function for consistency with other auto layout changes
                              autoLayoutCallbacks.handleResizingChange(
                                "width",
                                "fixed",
                              );
                            }
                          }}
                          title="Fixed width - Frame maintains exact width"
                        >
                          Fixed
                        </button>
                        {/* Show Fill option only when frame is nested inside an auto layout frame */}
                        {frameElement?.frameId &&
                          (() => {
                            const parentFrame = elementsMap.get(
                              frameElement.frameId,
                            );
                            return (
                              parentFrame &&
                              parentFrame.type === "frame" &&
                              (parentFrame as any).autoLayout?.enabled
                            );
                          })() && (
                            <button
                              type="button"
                              className={`auto-layout-resize-option ${
                                (frameElement as any).autoLayoutChildResizing
                                  ?.width === "fill"
                                  ? "active"
                                  : ""
                              }`}
                              onClick={() => {
                                if (autoLayoutCallbacks && frameElement) {
                                  const allElements =
                                    app.scene.getElementsIncludingDeleted();
                                  const isFrameInHugMode =
                                    frameElement.autoLayout?.resizing.width ===
                                    "hug";

                                  if (isFrameInHugMode) {
                                    // For hug->fill: Convert frame to fixed first, then set child to fill
                                    const updatedElements = allElements.map(
                                      (el) => {
                                        if (el.id === frameElement.id) {
                                          return {
                                            ...el,
                                            autoLayoutChildResizing: {
                                              width: "fill" as const,
                                              height:
                                                (frameElement as any)
                                                  .autoLayoutChildResizing
                                                  ?.height || "fixed",
                                            },
                                            autoLayout: {
                                              ...frameElement.autoLayout!,
                                              resizing: {
                                                ...frameElement.autoLayout!
                                                  .resizing,
                                                width: "fixed" as const,
                                              },
                                            },
                                          };
                                        }
                                        return el;
                                      },
                                    );

                                    // Apply auto layout to all frames after updating properties (matching child resizing pattern)
                                    const elementsMap1 =
                                      app.scene.getNonDeletedElementsMap();
                                    const finalElements1 =
                                      reapplyAutoLayoutToAllFrames(
                                        updatedElements,
                                        elementsMap1,
                                      );
                                    app.scene.replaceAllElements(
                                      finalElements1,
                                    );
                                    app.scene.triggerUpdate();

                                    // Trigger additional auto layout rerendering to ensure all nested elements are positioned correctly
                                    setTimeout(() => {
                                      const currentElements =
                                        app.scene.getElementsIncludingDeleted();
                                      const currentElementsMap =
                                        app.scene.getNonDeletedElementsMap();
                                      const rerenderedElements =
                                        reapplyAutoLayoutToAllFrames(
                                          currentElements,
                                          currentElementsMap,
                                        );
                                      if (
                                        rerenderedElements !== currentElements
                                      ) {
                                        app.scene.replaceAllElements(
                                          rerenderedElements,
                                        );
                                      }
                                    }, 0);
                                  } else {
                                    // Frame is already fixed, just set child to fill
                                    const fillElements = allElements.map(
                                      (el) => {
                                        if (el.id === frameElement.id) {
                                          return {
                                            ...el,
                                            autoLayoutChildResizing: {
                                              width: "fill" as const,
                                              height:
                                                (frameElement as any)
                                                  .autoLayoutChildResizing
                                                  ?.height || "fixed",
                                            },
                                          };
                                        }
                                        return el;
                                      },
                                    );

                                    // Apply auto layout to all frames after updating properties (matching child resizing pattern)
                                    const elementsMap2 =
                                      app.scene.getNonDeletedElementsMap();
                                    const finalElements2 =
                                      reapplyAutoLayoutToAllFrames(
                                        fillElements,
                                        elementsMap2,
                                      );
                                    app.scene.replaceAllElements(
                                      finalElements2,
                                    );
                                    app.scene.triggerUpdate();

                                    // Trigger additional auto layout rerendering to ensure all nested elements are positioned correctly
                                    setTimeout(() => {
                                      const currentElements =
                                        app.scene.getElementsIncludingDeleted();
                                      const currentElementsMap =
                                        app.scene.getNonDeletedElementsMap();
                                      const rerenderedElements =
                                        reapplyAutoLayoutToAllFrames(
                                          currentElements,
                                          currentElementsMap,
                                        );
                                      if (
                                        rerenderedElements !== currentElements
                                      ) {
                                        app.scene.replaceAllElements(
                                          rerenderedElements,
                                        );
                                      }
                                    }, 0);
                                  }
                                }
                              }}
                              title="Fill container - Frame stretches to fill available width"
                            >
                              Fill
                            </button>
                          )}
                      </div>
                    </fieldset>
                    <fieldset className="auto-layout-section">
                      <legend>Height</legend>
                      <div className="auto-layout-resize-buttons">
                        <button
                          type="button"
                          className={`auto-layout-resize-option ${
                            (frameElement as any).autoLayoutChildResizing
                              ?.height === "fill"
                              ? ""
                              : frameElement?.autoLayout?.resizing.height ===
                                "hug"
                              ? "active"
                              : ""
                          }`}
                          onClick={() => {
                            if (autoLayoutCallbacks && frameElement) {
                              // Get the latest frame element from the scene to avoid stale closure references
                              const allElements =
                                app.scene.getElementsIncludingDeleted();
                              const latestFrame = allElements.find(
                                (el) => el.id === frameElement.id,
                              ) as ExcalidrawFrameElement;

                              if (
                                !latestFrame ||
                                !isFrameLikeElement(latestFrame)
                              ) {
                                return;
                              }

                              // If frame is currently in fill mode, clear the child resizing property first
                              if (
                                (frameElement as any).autoLayoutChildResizing
                                  ?.height === "fill"
                              ) {
                                const updatedElements = allElements.map(
                                  (el) => {
                                    if (el.id === frameElement.id) {
                                      return {
                                        ...el,
                                        autoLayoutChildResizing: {
                                          width:
                                            (frameElement as any)
                                              .autoLayoutChildResizing?.width ||
                                            "fixed",
                                          height: "fixed" as const,
                                        },
                                      };
                                    }
                                    return el;
                                  },
                                );

                                // Apply auto layout to all frames after clearing fill state
                                const elementsMap =
                                  app.scene.getNonDeletedElementsMap();
                                const finalElements =
                                  reapplyAutoLayoutToAllFrames(
                                    updatedElements,
                                    elementsMap,
                                  );
                                app.scene.replaceAllElements(finalElements);
                                app.scene.triggerUpdate();
                              }

                              // Use the callback function for consistency with other auto layout changes
                              autoLayoutCallbacks.handleResizingChange(
                                "height",
                                "hug",
                              );
                            }
                          }}
                          title="Hug contents - Frame resizes to fit children"
                        >
                          Hug
                        </button>
                        <button
                          type="button"
                          className={`auto-layout-resize-option ${
                            (frameElement as any).autoLayoutChildResizing
                              ?.height === "fill"
                              ? ""
                              : frameElement?.autoLayout?.resizing.height ===
                                "fixed"
                              ? "active"
                              : ""
                          }`}
                          onClick={() => {
                            if (autoLayoutCallbacks && frameElement) {
                              // Get the latest frame element from the scene to avoid stale closure references
                              const allElements =
                                app.scene.getElementsIncludingDeleted();
                              const latestFrame = allElements.find(
                                (el) => el.id === frameElement.id,
                              ) as ExcalidrawFrameElement;

                              if (
                                !latestFrame ||
                                !isFrameLikeElement(latestFrame)
                              ) {
                                return;
                              }

                              // If frame is currently in fill mode, clear the child resizing property first
                              if (
                                (frameElement as any).autoLayoutChildResizing
                                  ?.height === "fill"
                              ) {
                                const updatedElements = allElements.map(
                                  (el) => {
                                    if (el.id === frameElement.id) {
                                      return {
                                        ...el,
                                        autoLayoutChildResizing: {
                                          width:
                                            (frameElement as any)
                                              .autoLayoutChildResizing?.width ||
                                            "fixed",
                                          height: "fixed" as const,
                                        },
                                      };
                                    }
                                    return el;
                                  },
                                );

                                // Apply auto layout to all frames after clearing fill state
                                const elementsMap =
                                  app.scene.getNonDeletedElementsMap();
                                const finalElements =
                                  reapplyAutoLayoutToAllFrames(
                                    updatedElements,
                                    elementsMap,
                                  );
                                app.scene.replaceAllElements(finalElements);
                                app.scene.triggerUpdate();
                              }

                              // Use the callback function for consistency with other auto layout changes
                              autoLayoutCallbacks.handleResizingChange(
                                "height",
                                "fixed",
                              );
                            }
                          }}
                          title="Fixed height - Frame maintains exact height"
                        >
                          Fixed
                        </button>
                        {/* Show Fill option only when frame is nested inside an auto layout frame */}
                        {frameElement?.frameId &&
                          (() => {
                            const parentFrame = elementsMap.get(
                              frameElement.frameId,
                            );
                            return (
                              parentFrame &&
                              parentFrame.type === "frame" &&
                              (parentFrame as any).autoLayout?.enabled
                            );
                          })() && (
                            <button
                              type="button"
                              className={`auto-layout-resize-option ${
                                (frameElement as any).autoLayoutChildResizing
                                  ?.height === "fill"
                                  ? "active"
                                  : ""
                              }`}
                              onClick={() => {
                                if (autoLayoutCallbacks && frameElement) {
                                  const allElements =
                                    app.scene.getElementsIncludingDeleted();
                                  const isFrameInHugMode =
                                    frameElement.autoLayout?.resizing.height ===
                                    "hug";

                                  if (isFrameInHugMode) {
                                    // For hug->fill: Convert frame to fixed first, then set child to fill
                                    const finalElements = allElements.map(
                                      (el) => {
                                        if (el.id === frameElement.id) {
                                          return {
                                            ...el,
                                            autoLayoutChildResizing: {
                                              width:
                                                (frameElement as any)
                                                  .autoLayoutChildResizing
                                                  ?.width || "fixed",
                                              height: "fill" as const,
                                            },
                                            autoLayout: {
                                              ...frameElement.autoLayout!,
                                              resizing: {
                                                ...frameElement.autoLayout!
                                                  .resizing,
                                                height: "fixed" as const,
                                              },
                                            },
                                          };
                                        }
                                        return el;
                                      },
                                    );

                                    // Apply auto layout to all frames after updating properties (matching child resizing pattern)
                                    const elementsMap3 =
                                      app.scene.getNonDeletedElementsMap();
                                    const finalElements3 =
                                      reapplyAutoLayoutToAllFrames(
                                        finalElements,
                                        elementsMap3,
                                      );
                                    app.scene.replaceAllElements(
                                      finalElements3,
                                    );
                                    app.scene.triggerUpdate();

                                    // Trigger additional auto layout rerendering to ensure all nested elements are positioned correctly
                                    setTimeout(() => {
                                      const currentElements =
                                        app.scene.getElementsIncludingDeleted();
                                      const currentElementsMap =
                                        app.scene.getNonDeletedElementsMap();
                                      const rerenderedElements =
                                        reapplyAutoLayoutToAllFrames(
                                          currentElements,
                                          currentElementsMap,
                                        );
                                      if (
                                        rerenderedElements !== currentElements
                                      ) {
                                        app.scene.replaceAllElements(
                                          rerenderedElements,
                                        );
                                      }
                                    }, 0);
                                  } else {
                                    // Frame is already fixed, just set child to fill
                                    const fillElements = allElements.map(
                                      (el) => {
                                        if (el.id === frameElement.id) {
                                          return {
                                            ...el,
                                            autoLayoutChildResizing: {
                                              width:
                                                (frameElement as any)
                                                  .autoLayoutChildResizing
                                                  ?.width || "fixed",
                                              height: "fill" as const,
                                            },
                                          };
                                        }
                                        return el;
                                      },
                                    );

                                    // Apply auto layout to all frames after updating properties (matching child resizing pattern)
                                    const elementsMap4 =
                                      app.scene.getNonDeletedElementsMap();
                                    const finalElements4 =
                                      reapplyAutoLayoutToAllFrames(
                                        fillElements,
                                        elementsMap4,
                                      );
                                    app.scene.replaceAllElements(
                                      finalElements4,
                                    );
                                    app.scene.triggerUpdate();

                                    // Trigger additional auto layout rerendering to ensure all nested elements are positioned correctly
                                    setTimeout(() => {
                                      const currentElements =
                                        app.scene.getElementsIncludingDeleted();
                                      const currentElementsMap =
                                        app.scene.getNonDeletedElementsMap();
                                      const rerenderedElements =
                                        reapplyAutoLayoutToAllFrames(
                                          currentElements,
                                          currentElementsMap,
                                        );
                                      if (
                                        rerenderedElements !== currentElements
                                      ) {
                                        app.scene.replaceAllElements(
                                          rerenderedElements,
                                        );
                                      }
                                    }, 0);
                                  }
                                }
                              }}
                              title="Fill container - Frame stretches to fill available height"
                            >
                              Fill
                            </button>
                          )}
                      </div>
                    </fieldset>
                  </div>

                  {/* Right Column: Alignment & Gap */}
                  <div className="alignment-column">
                    <fieldset className="auto-layout-section">
                      <legend>Alignment</legend>
                      <div className="auto-layout-alignment">
                        {frameElement && autoLayoutCallbacks && (
                          <AlignmentGrid
                            frameElement={frameElement}
                            autoLayoutCallbacks={autoLayoutCallbacks}
                            hoveredAlignmentPosition={hoveredAlignmentPosition}
                            setHoveredAlignmentPosition={
                              setHoveredAlignmentPosition
                            }
                          />
                        )}
                      </div>
                    </fieldset>
                  </div>
                </div>

                {/* Resizing - No legend, matching Flow section style */}
                <fieldset
                  className="auto-layout-section"
                  style={{ display: "none" }}
                >
                  <div className="alignment-section-dual">
                    <div className="alignment-column">
                      <span className="auto-layout-legend">Width</span>
                      <div className="auto-layout-resize-buttons">
                        <button
                          type="button"
                          className={`auto-layout-resize-option ${
                            frameElement?.autoLayout?.resizing.width === "hug"
                              ? "active"
                              : ""
                          }`}
                          onClick={() => {
                            if (autoLayoutCallbacks) {
                              autoLayoutCallbacks.handleResizingChange(
                                "width",
                                "hug",
                              );
                            }
                          }}
                          title="Auto size - Frame resizes to fit children"
                        >
                          Auto
                        </button>
                        <button
                          type="button"
                          className={`auto-layout-resize-option ${
                            frameElement?.autoLayout?.resizing.width === "fixed"
                              ? "active"
                              : ""
                          }`}
                          onClick={() => {
                            if (autoLayoutCallbacks) {
                              autoLayoutCallbacks.handleResizingChange(
                                "width",
                                "fixed",
                              );
                            }
                          }}
                          title="Fixed width - Frame maintains exact width"
                        >
                          Fixed
                        </button>
                      </div>
                    </div>
                    <div className="alignment-column">
                      <span className="auto-layout-legend">Height</span>
                      <div className="auto-layout-resize-buttons">
                        <button
                          type="button"
                          className={`auto-layout-resize-option ${
                            frameElement?.autoLayout?.resizing.height === "hug"
                              ? "active"
                              : ""
                          }`}
                          onClick={() => {
                            if (autoLayoutCallbacks) {
                              autoLayoutCallbacks.handleResizingChange(
                                "height",
                                "hug",
                              );
                            }
                          }}
                          title="Auto size - Frame resizes to fit children"
                        >
                          Auto
                        </button>
                        <button
                          type="button"
                          className={`auto-layout-resize-option ${
                            frameElement?.autoLayout?.resizing.height ===
                            "fixed"
                              ? "active"
                              : ""
                          }`}
                          onClick={() => {
                            if (autoLayoutCallbacks) {
                              autoLayoutCallbacks.handleResizingChange(
                                "height",
                                "fixed",
                              );
                            }
                          }}
                          title="Fixed height - Frame maintains exact height"
                        >
                          Fixed
                        </button>
                      </div>
                    </div>
                  </div>
                </fieldset>

                {/* Gap and Padding - 2-column layout */}
                <fieldset
                  className="auto-layout-section"
                  style={{ margin: "8px 0" }}
                >
                  <div className="alignment-section-dual">
                    {/* Gap column with integrated settings icon */}
                    <div className="alignment-column">
                      <legend>Gap</legend>
                      <div
                        className="drag-input-container"
                        data-testid="Gap"
                        title="Auto layout gap"
                      >
                        <DragInput
                          label="G"
                          icon={SpacingHorizontalIcon}
                          elements={frameElement ? [frameElement] : []}
                          dragInputCallback={(props) => {
                            if (frameElement && autoLayoutCallbacks) {
                              const rawCurrent =
                                frameElement.autoLayout?.gap ?? 0;
                              const numericCurrent =
                                typeof rawCurrent === "number" ? rawCurrent : 0;
                              const newValue =
                                props.nextValue !== undefined
                                  ? props.nextValue
                                  : numericCurrent + props.accumulatedChange;
                              autoLayoutCallbacks.handleAutoLayoutGapChange(
                                newValue,
                              );
                            }
                          }}
                          value={
                            frameElement?.autoLayout?.gap === "auto"
                              ? ("Auto" as any)
                              : typeof frameElement?.autoLayout?.gap ===
                                "number"
                              ? frameElement?.autoLayout?.gap
                              : 10
                          }
                          editable={true}
                          scene={scene}
                          appState={fullAppState}
                          property="gridStep"
                          dragFinishedCallback={() => {
                            if (autoLayoutCallbacks) {
                              autoLayoutCallbacks.handleAutoLayoutGapDragFinished();
                            }
                          }}
                          tooltip="Auto layout gap"
                        />
                        <button
                          className="drag-input-icon-right-button"
                          type="button"
                          onClick={() => {
                            setShowGapOptions(!showGapOptions);
                            if (!showGapOptions) {
                              setShowDetailedPadding(false);
                            }
                          }}
                        >
                          <InlineIcon icon={adjustmentsHorizontalIcon} />
                        </button>
                      </div>

                      {/* Gap Type Settings - Collapsible */}
                      {showGapOptions && (
                        <div
                          style={{
                            gridColumn: "1 / -1",
                            marginTop: "8px",
                          }}
                        >
                          <div
                            className="auto-layout-resize-buttons"
                            style={{ display: "flex", gap: "1px" }}
                          >
                            <button
                              type="button"
                              className={`auto-layout-resize-option ${
                                frameElement?.autoLayout?.gap === "auto"
                                  ? "active"
                                  : ""
                              }`}
                              onClick={() => {
                                if (autoLayoutCallbacks && frameElement) {
                                  autoLayoutCallbacks.handleAutoLayoutGapTypeChange(
                                    "auto",
                                  );
                                }
                              }}
                              title="Auto gap - Distributes elements evenly (space-between)"
                              style={{ flex: 1 }}
                            >
                              Auto
                            </button>
                            <button
                              type="button"
                              className={`auto-layout-resize-option ${
                                typeof frameElement?.autoLayout?.gap ===
                                "number"
                                  ? "active"
                                  : ""
                              }`}
                              onClick={() => {
                                if (autoLayoutCallbacks && frameElement) {
                                  const currentGap =
                                    frameElement.autoLayout?.gap;
                                  const numericGap =
                                    typeof currentGap === "number"
                                      ? currentGap
                                      : 10;
                                  autoLayoutCallbacks.handleAutoLayoutGapTypeChange(
                                    numericGap,
                                  );
                                }
                              }}
                              title="Fixed gap - Uses specific pixel value"
                              style={{ flex: 1 }}
                            >
                              Fixed
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Global Padding column with integrated settings icon */}
                    <div className="alignment-column">
                      <legend>Padding</legend>
                      <div
                        className="drag-input-container"
                        data-testid="Padding"
                        title="Global padding for all sides"
                      >
                        <DragInput
                          label={<InlineIcon icon={paddingAllIcon} />}
                          elements={frameElement ? [frameElement] : []}
                          dragInputCallback={({
                            nextValue,
                            accumulatedChange,
                          }) => {
                            if (frameElement && autoLayoutCallbacks) {
                              const current =
                                frameElement.autoLayout?.padding.top ?? 0;
                              const value =
                                nextValue !== undefined
                                  ? nextValue
                                  : current + accumulatedChange;
                              autoLayoutCallbacks.handleBulkAutoLayoutPaddingChange(
                                {
                                  top: value,
                                  right: value,
                                  bottom: value,
                                  left: value,
                                },
                              );
                            }
                          }}
                          value={
                            frameElement &&
                            frameElement.autoLayout?.padding.top ===
                              frameElement.autoLayout?.padding.right &&
                            frameElement.autoLayout?.padding.right ===
                              frameElement.autoLayout?.padding.bottom &&
                            frameElement.autoLayout?.padding.bottom ===
                              frameElement.autoLayout?.padding.left
                              ? frameElement.autoLayout?.padding.top ?? 0
                              : "Mixed"
                          }
                          editable={!!frameElement}
                          scene={scene}
                          appState={fullAppState}
                          property="gridStep"
                          dragFinishedCallback={() => {
                            /* No-op: updates applied in callback */
                          }}
                          tooltip="Global padding for all sides"
                        />
                        <button
                          className="drag-input-icon-right-button"
                          type="button"
                          onClick={() => {
                            setShowDetailedPadding(!showDetailedPadding);
                            if (!showDetailedPadding) {
                              setShowGapOptions(false);
                            }
                          }}
                        >
                          <InlineIcon icon={adjustmentsHorizontalIcon} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Detailed Padding Settings - Collapsible */}
                  {showDetailedPadding && (
                    <div
                      className="auto-layout-padding"
                      style={{ marginTop: "8px" }}
                    >
                      <div className="auto-layout-padding-row">
                        <div className="property-item">
                          <DragInput
                            label="T"
                            icon={paddingTopIcon}
                            elements={frameElement ? [frameElement] : []}
                            dragInputCallback={(props) => {
                              if (frameElement && autoLayoutCallbacks) {
                                const current =
                                  frameElement.autoLayout?.padding.top ?? 0;
                                const newValue =
                                  props.nextValue !== undefined
                                    ? props.nextValue
                                    : current + props.accumulatedChange;
                                autoLayoutCallbacks.handleAutoLayoutPaddingChange(
                                  "top",
                                  newValue,
                                );
                              }
                            }}
                            value={frameElement?.autoLayout?.padding.top ?? 16}
                            editable={true}
                            scene={scene}
                            appState={fullAppState}
                            property="gridStep"
                            dragFinishedCallback={() => {
                              if (autoLayoutCallbacks) {
                                autoLayoutCallbacks.handleAutoLayoutPaddingDragFinished();
                              }
                            }}
                            tooltip="Top padding"
                          />
                        </div>
                        <div className="property-item">
                          <DragInput
                            label="R"
                            icon={paddingRightIcon}
                            elements={frameElement ? [frameElement] : []}
                            dragInputCallback={(props) => {
                              if (frameElement && autoLayoutCallbacks) {
                                const current =
                                  frameElement.autoLayout?.padding.right ?? 0;
                                const newValue =
                                  props.nextValue !== undefined
                                    ? props.nextValue
                                    : current + props.accumulatedChange;
                                autoLayoutCallbacks.handleAutoLayoutPaddingChange(
                                  "right",
                                  newValue,
                                );
                              }
                            }}
                            value={
                              frameElement?.autoLayout?.padding.right ?? 16
                            }
                            editable={true}
                            scene={scene}
                            appState={fullAppState}
                            property="gridStep"
                            dragFinishedCallback={() => {
                              if (autoLayoutCallbacks) {
                                autoLayoutCallbacks.handleAutoLayoutPaddingDragFinished();
                              }
                            }}
                            tooltip="Right padding"
                          />
                        </div>
                      </div>
                      <div className="auto-layout-padding-row">
                        <div className="property-item">
                          <DragInput
                            label="B"
                            icon={paddingBottomIcon}
                            elements={frameElement ? [frameElement] : []}
                            dragInputCallback={(props) => {
                              if (frameElement && autoLayoutCallbacks) {
                                const current =
                                  frameElement.autoLayout?.padding.bottom ?? 0;
                                const newValue =
                                  props.nextValue !== undefined
                                    ? props.nextValue
                                    : current + props.accumulatedChange;
                                autoLayoutCallbacks.handleAutoLayoutPaddingChange(
                                  "bottom",
                                  newValue,
                                );
                              }
                            }}
                            value={
                              frameElement?.autoLayout?.padding.bottom ?? 16
                            }
                            editable={true}
                            scene={scene}
                            appState={fullAppState}
                            property="gridStep"
                            dragFinishedCallback={() => {
                              if (autoLayoutCallbacks) {
                                autoLayoutCallbacks.handleAutoLayoutPaddingDragFinished();
                              }
                            }}
                            tooltip="Bottom padding"
                          />
                        </div>
                        <div className="property-item">
                          <DragInput
                            label="L"
                            icon={paddingLeftIcon}
                            elements={frameElement ? [frameElement] : []}
                            dragInputCallback={(props) => {
                              if (frameElement && autoLayoutCallbacks) {
                                const current =
                                  frameElement.autoLayout?.padding.left ?? 0;
                                const newValue =
                                  props.nextValue !== undefined
                                    ? props.nextValue
                                    : current + props.accumulatedChange;
                                autoLayoutCallbacks.handleAutoLayoutPaddingChange(
                                  "left",
                                  newValue,
                                );
                              }
                            }}
                            value={frameElement?.autoLayout?.padding.left ?? 16}
                            editable={true}
                            scene={scene}
                            appState={fullAppState}
                            property="gridStep"
                            dragFinishedCallback={() => {
                              if (autoLayoutCallbacks) {
                                autoLayoutCallbacks.handleAutoLayoutPaddingDragFinished();
                              }
                            }}
                            tooltip="Left padding"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </fieldset>

                {/* Move position section for nested frames with auto layout */}
                {frameElement &&
                  frameElement.frameId &&
                  (() => {
                    // Check if this frame is a child of an auto layout frame
                    const parentFrame = elementsMap.get(frameElement.frameId);
                    if (
                      !parentFrame ||
                      !isFrameLikeElement(parentFrame) ||
                      !(parentFrame as ExcalidrawFrameElement).autoLayout
                        ?.enabled
                    ) {
                      return null;
                    }

                    const parentAutoLayoutFrame =
                      parentFrame as ExcalidrawFrameElement;
                    if (!parentAutoLayoutFrame.autoLayout) {
                      return null;
                    }
                    const isHorizontal =
                      parentAutoLayoutFrame.autoLayout.direction ===
                      "horizontal";

                    const handleReorder = (
                      operation: "first" | "last" | "previous" | "next",
                    ) => {
                      const allElements =
                        app.scene.getElementsIncludingDeleted();
                      const newElements = reorderAutoLayoutElements(
                        parentAutoLayoutFrame,
                        [frameElement],
                        operation,
                        allElements,
                        app.scene.getNonDeletedElementsMap(),
                      );
                      app.scene.replaceAllElements(newElements);

                      // Record the change in undo/redo history
                      app.syncActionResult({
                        captureUpdate: CaptureUpdateAction.IMMEDIATELY,
                      });

                      // Trigger additional auto layout rerendering to ensure all layout (padding, gaps) is properly applied
                      setTimeout(() => {
                        const currentElements =
                          app.scene.getElementsIncludingDeleted();
                        const currentElementsMap =
                          app.scene.getNonDeletedElementsMap();
                        const rerenderedElements = reapplyAutoLayoutToAllFrames(
                          currentElements,
                          currentElementsMap,
                        );
                        if (rerenderedElements !== currentElements) {
                          app.scene.replaceAllElements(rerenderedElements);
                        }

                        // Final trigger to ensure visual re-rendering after auto-layout changes
                        app.scene.triggerUpdate();
                      }, 0);
                    };

                    return (
                      <fieldset className="auto-layout-section">
                        <legend>Move position</legend>
                        <div className="auto-layout-order-buttons">
                          <button
                            type="button"
                            className="auto-layout-order-button"
                            onClick={() => handleReorder("first")}
                            title={
                              isHorizontal
                                ? "Move to leftmost position"
                                : "Move to top position"
                            }
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              {isHorizontal ? (
                                // Horizontal: Move to left
                                <>
                                  <rect
                                    x="2"
                                    y="4"
                                    width="2"
                                    height="8"
                                    fill="currentColor"
                                  />
                                  <path
                                    d="M6 8L10 4V12L6 8Z"
                                    fill="currentColor"
                                  />
                                </>
                              ) : (
                                // Vertical: Move to top
                                <>
                                  <rect
                                    x="4"
                                    y="2"
                                    width="8"
                                    height="2"
                                    fill="currentColor"
                                  />
                                  <path
                                    d="M8 6L4 10H12L8 6Z"
                                    fill="currentColor"
                                  />
                                </>
                              )}
                            </svg>
                            First
                          </button>

                          <button
                            type="button"
                            className="auto-layout-order-button"
                            onClick={() => handleReorder("previous")}
                            title={isHorizontal ? "Move left" : "Move up"}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              {isHorizontal ? (
                                // Left arrow
                                <path
                                  d="M10 4L6 8L10 12"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              ) : (
                                // Up arrow
                                <path
                                  d="M4 10L8 6L12 10"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              )}
                            </svg>
                            {isHorizontal ? "Left" : "Up"}
                          </button>

                          <button
                            type="button"
                            className="auto-layout-order-button"
                            onClick={() => handleReorder("next")}
                            title={isHorizontal ? "Move right" : "Move down"}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              {isHorizontal ? (
                                // Right arrow
                                <path
                                  d="M6 4L10 8L6 12"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              ) : (
                                // Down arrow
                                <path
                                  d="M12 6L8 10L4 6"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              )}
                            </svg>
                            {isHorizontal ? "Right" : "Down"}
                          </button>

                          <button
                            type="button"
                            className="auto-layout-order-button"
                            onClick={() => handleReorder("last")}
                            title={
                              isHorizontal
                                ? "Move to rightmost position"
                                : "Move to bottom position"
                            }
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              {isHorizontal ? (
                                // Horizontal: Move to right
                                <>
                                  <rect
                                    x="12"
                                    y="4"
                                    width="2"
                                    height="8"
                                    fill="currentColor"
                                  />
                                  <path
                                    d="M10 8L6 4V12L10 8Z"
                                    fill="currentColor"
                                  />
                                </>
                              ) : (
                                // Vertical: Move to bottom
                                <>
                                  <rect
                                    x="4"
                                    y="12"
                                    width="8"
                                    height="2"
                                    fill="currentColor"
                                  />
                                  <path
                                    d="M8 10L4 6H12L8 10Z"
                                    fill="currentColor"
                                  />
                                </>
                              )}
                            </svg>
                            Last
                          </button>
                        </div>
                      </fieldset>
                    );
                  })()}
              </>
            )}
          </div>
        </PropertySection>
      )}

      {/* Stroke Section */}
      {(canChangeStrokeColor(appState, targetElements) ||
        hasStrokeWidth(appState.activeTool.type) ||
        targetElements.some((element) => hasStrokeWidth(element.type)) ||
        hasStrokeStyle(appState.activeTool.type) ||
        targetElements.some((element) => hasStrokeStyle(element.type)) ||
        canChangeRoundness(appState.activeTool.type) ||
        targetElements.some((element) => canChangeRoundness(element.type))) && (
        <PropertySection showSeparator={false}>
          {canChangeStrokeColor(appState, targetElements) &&
            renderAction("changeStrokeColor")}

          {/* 2-column layout for Line width and Brush style */}
          {(hasStrokeWidth(appState.activeTool.type) ||
            targetElements.some((element) => hasStrokeWidth(element.type))) && (
            <div className="stroke-section-dual">
              {/* Line width column */}
              {(hasStrokeWidth(appState.activeTool.type) ||
                targetElements.some((element) =>
                  hasStrokeWidth(element.type),
                )) && (
                <div className="stroke-column">
                  {renderAction("changeStrokeWidth")}
                </div>
              )}

              {/* Brush style column */}
              {targetElements.some(
                (element) =>
                  element.hasOwnProperty("roughness") &&
                  element.type !== "frame" &&
                  element.type !== "magicframe",
              ) && (
                <div className="stroke-column">
                  {renderAction("changeSloppiness")}
                </div>
              )}
            </div>
          )}

          {(hasStrokeStyle(appState.activeTool.type) ||
            targetElements.some((element) => hasStrokeStyle(element.type))) && (
            <div style={{ marginTop: "0.5rem" }}>
              <PropertySection showSeparator={true}>
                <Collapsible
                  className="mobile-more-options"
                  label={
                    <span
                      style={{
                        fontSize: "0.75rem",
                        color: "var(--text-primary-color)",
                        fontWeight: 400,
                      }}
                    >
                      More options
                    </span>
                  }
                  open={showMoreOptions}
                  openTrigger={() => setShowMoreOptions(!showMoreOptions)}
                >
                  <div className="stroke-options-container">
                    {renderAction("changeStrokeStyle")}
                  </div>
                </Collapsible>
              </PropertySection>
            </div>
          )}
        </PropertySection>
      )}

      {/* Background Section */}
      {(canChangeBackgroundColor(appState, targetElements) ||
        showFillIcons) && (
        <PropertySection>
          {canChangeBackgroundColor(appState, targetElements) && (
            <div>{renderAction("changeBackgroundColor")}</div>
          )}
          {showFillIcons && renderAction("changeFillStyle")}
        </PropertySection>
      )}

      {/* Arrow Section - Combined Arrow Type and Arrowhead in dual columns */}
      {(toolIsArrow(appState.activeTool.type) ||
        targetElements.some((element) => toolIsArrow(element.type)) ||
        canHaveArrowheads(appState.activeTool.type) ||
        targetElements.some((element) => canHaveArrowheads(element.type))) && (
        <PropertySection>
          <div className="arrow-section-dual">
            {/* Arrow Type column */}
            {(toolIsArrow(appState.activeTool.type) ||
              targetElements.some((element) => toolIsArrow(element.type))) && (
              <div className="arrow-column">
                {renderAction("changeArrowType")}
              </div>
            )}

            {/* Arrowhead column */}
            {(canHaveArrowheads(appState.activeTool.type) ||
              targetElements.some((element) =>
                canHaveArrowheads(element.type),
              )) && (
              <div className="arrow-column">
                {renderAction("changeArrowhead")}
              </div>
            )}
          </div>
        </PropertySection>
      )}

      {/* Font Section */}
      {(appState.activeTool.type === "text" ||
        targetElements.some(isTextElement)) && (
        <PropertySection>
          {renderAction("changeFontFamily")}
          {renderAction("changeFontSize")}
          {(appState.activeTool.type === "text" ||
            suppportsHorizontalAlign(targetElements, elementsMap) ||
            shouldAllowVerticalAlign(targetElements, elementsMap)) && (
            <fieldset style={{ marginTop: "0.25rem" }}>
              <legend>{t("labels.textAlign")}</legend>
              <div
                style={{
                  display: "flex",
                  gap: "0.5rem",
                  alignItems: "flex-start",
                  flexWrap: "nowrap",
                  width: "100%",
                }}
              >
                {(appState.activeTool.type === "text" ||
                  suppportsHorizontalAlign(targetElements, elementsMap)) && (
                  <div className="buttonList text-align-buttons">
                    {renderAction("changeTextAlign")}
                  </div>
                )}
                {shouldAllowVerticalAlign(targetElements, elementsMap) && (
                  <div className="buttonList text-align-buttons">
                    {renderAction("changeVerticalAlign")}
                  </div>
                )}
              </div>
            </fieldset>
          )}
        </PropertySection>
      )}

      {/* Layers Section */}
      <PropertySection>
        <div
          style={{
            fontWeight: 600,
            fontSize: "0.8rem",
            marginBottom: "0.25rem",
          }}
        >
          {t("labels.layers")}
        </div>
        <div className="buttonList">
          {renderAction("sendToBack")}
          {renderAction("sendBackward")}
          {renderAction("bringForward")}
          {renderAction("bringToFront")}
        </div>
      </PropertySection>

      {/* Actions Section - moved to last */}
      {!isEditingTextOrNewElement && targetElements.length > 0 && (
        <PropertySection
          legend={t("labels.actions")}
          showSeparator={false}
          className="last-section"
        >
          <div className="buttonList">
            {!device.editor.isMobile && renderAction("duplicateSelection")}
            {!device.editor.isMobile && renderAction("deleteSelectedElements")}
            {renderAction("group")}
            {renderAction("ungroup")}
            {showLinkIcon && renderAction("hyperlink")}
            {showCropEditorAction && renderAction("cropEditor")}
            {showLineEditorAction && renderAction("toggleLinearEditor")}
          </div>
        </PropertySection>
      )}
    </div>
  );
};
